import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import * as logger from 'loglevel';
import { journeyTypeToLongValue, journeyTypeToShortValue } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { annotateAndLogError } from '../../../helpers/error';
import { NOT_AVAILABLE, EMPTY_OBJECT, formatValue, formatDate } from './helpers';
import { useTracking } from './TrackingContext';
import { publish } from './pubsub';
import { doRedirect } from './redirects';
import { getAirports } from './../../../_integrations/everymundo/airports';

export { TrackingProvider } from './TrackingContext';

const OriginDestinationLogic = {
  SAME: 'same',
  DIFFERENT: 'different'
};

type flightTypeOptions = {
  [key: string]: string;
};

type originDestinationInfo = {
  city?: { name: string };
  extraInfo?: { type?: string; zoneId?: string; locationId?: string };
};

const flightTypeMap: flightTypeOptions = {
  rt: 'ROUND_TRIP',
  ow: 'ONE_WAY'
};
type Vacation = {
  air_room_car_radio?: string;
  area1?: string;
  area2?: string;
  date1?: string;
  date2?: string;
  flight?: boolean;
  hotel?: boolean;
  numRooms?: number;
  rooms?: {
    childAges?: number[];
    numAdults?: number;
    numChildren?: number;
    numInfantsInLaps?: number;
    numInfantsInSeats?: number;
    numYouths?: number;
  }[];
};

interface QueryType {
  origin?: string;
  destination?: string;
  departure?: string;
  return?: string;
  flightType?: string;
  siteEdition?: string;
  language?: string;
  passengers?: Record<string, any>;
  currencyCode?: string;
  travelClass?: string;
  promoCode?: string;
  flexibleDates?: boolean;
  device?: string;
  extraInfo?: Record<string, any>;
  trackingParameters?: Record<string, any>;
  airlines?: string;
  originInfo?: originDestinationInfo;
  destinationInfo?: originDestinationInfo;
  vacations?: Vacation;
}

export function useContainerProps({
  fareItemInteractionType = 'popup',
  integrationSettings = EMPTY_OBJECT,
  meta = EMPTY_OBJECT,
  pageContext = EMPTY_OBJECT,
  prePopulation = EMPTY_OBJECT,
  tracking = EMPTY_OBJECT,
  filters = EMPTY_OBJECT,
  secondaryFilters = EMPTY_OBJECT,
  originDestinationLogic = OriginDestinationLogic.SAME
}: $TSFixMe) {
  let flexibleDates: boolean | 'n/a';
  const newFilters = { ...filters, ...secondaryFilters };

  if (newFilters?.dateRangeSettings?.enableFlexibleDates === true) {
    flexibleDates = !!newFilters?.dateRangeSettings?.checkedFlexibleDates;
  } else {
    flexibleDates = tracking.flexibleDates || 'n/a';
  }

  return React.useMemo(() => {
    return {
      fareItemInteractionType,

      // Tracking props
      eventType: tracking.eventType,
      eventCategory: tracking.eventCategory,
      flexibleDates: flexibleDates,
      passengerCount: tracking.passengerCount,
      promoCode: tracking.promoCode,
      trackingConfig: tracking.trackingConfig,
      useMiles: Boolean(prePopulation?.redemptionUnit),

      // Prepopulation props
      airlineIataCode: prePopulation.airlineIataCode,
      flightType: prePopulation.flightType,
      departureDateStart: prePopulation.departure?.start ?? null,
      returnDateEnd: prePopulation.return?.end ?? null,
      language: prePopulation.languageCode,
      currencyCode: formatValue(prePopulation.outputCurrencies),
      fareClass: formatValue(prePopulation.travelClasses),
      journeyType: formatValue(prePopulation.flightType),
      forceAirline: prePopulation.forceAirline,

      // Page context
      deviceCategory: pageContext.deviceCategory,
      siteEdition: pageContext.siteEdition,

      // Integration settings
      v2DeepLinkApiSettings: integrationSettings.v2DeepLinkApiSettings
        ? integrationSettings.v2DeepLinkApiSettings
        : null,
      integrationSettings,

      // Component metadata
      tagName: meta.tag,
      meta,
      prePopulation,
      pageContext,
      originDestinationLogic
    };
  }, [
    fareItemInteractionType,
    integrationSettings,
    meta,
    pageContext,
    prePopulation,
    tracking,
    flexibleDates,
    originDestinationLogic
  ]);
}

/**
 * This function get params in url
 * @param {*} query
 */

const getQueryParams = (query: $TSFixMe) => {
  const vars = query.split('&');
  const queryString: $TSFixMe = {};
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    const key = decodeURIComponent(pair[0]);
    const value = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof queryString[key] === 'undefined') {
      queryString[key] = decodeURIComponent(value);
      // If second entry with this name
    } else if (typeof queryString[key] === 'string') {
      const arr = [queryString[key], decodeURIComponent(value)];
      queryString[key] = arr;
      // If third or later entry with this name
    } else {
      queryString[key].push(decodeURIComponent(value));
    }
  }
  return queryString;
};

/**
 * This function get params in url that contains in white list
 * @param {*} trackingParametersWhitelist
 */
function getUrlParameters(trackingParametersWhitelist: $TSFixMe) {
  if (isSSR()) {
    return {};
  }
  const trackingParameters: $TSFixMe = {};
  if (Array.isArray(trackingParametersWhitelist)) {
    const query =
      window.location.search && window.location.search.startsWith('?')
        ? window.location.search.substring(1)
        : window.location.search;
    const params = getQueryParams(query);
    Object.keys(params).map((key) => {
      if (trackingParametersWhitelist.includes(key) && params[key] !== '') {
        trackingParameters[key] = params[key];
      }
      return key;
    });
  }
  return trackingParameters;
}

function getStaticParameters(v2DeepLinkApiSettings: $TSFixMe) {
  let params: $TSFixMe = {};
  if (
    v2DeepLinkApiSettings &&
    typeof v2DeepLinkApiSettings === 'object' &&
    v2DeepLinkApiSettings.constructor === Object
  ) {
    params = getUrlParameters(v2DeepLinkApiSettings.trackingParametersWhitelist);
    if (
      v2DeepLinkApiSettings.params &&
      v2DeepLinkApiSettings.params.trackingParameters &&
      typeof v2DeepLinkApiSettings.params.trackingParameters === 'object' &&
      v2DeepLinkApiSettings.params.trackingParameters.constructor === Object
    ) {
      params = Object.assign({}, params, v2DeepLinkApiSettings.params.trackingParameters);
      Object.keys(params).map((tp) => {
        if (params[tp] === '') {
          delete params[tp];
        }
        return tp;
      });
    }
  }
  return params;
}

export function getPopupData({ container, fare, state }: $TSFixMe) {
  const { origin, destination, flightType, journeyType, currencyCode, travelClass } = state;
  const { airlineIataCode, passengerCount, v2DeepLinkApiSettings, useMiles } = container;

  let newFlightType = formatValue(flightType);
  if (
    (fare && fare.origin !== undefined && fare.dataType === undefined) ||
    (fare && fare.origin !== undefined && fare.dataType === 'DATA_API')
  ) {
    //exist fare data
    if (fare.journeyType) {
      newFlightType =
        flightTypeMap[fare.journeyType] === undefined
          ? fare.journeyType
          : flightTypeMap[fare.journeyType];
    } else {
      if (container.flightType !== undefined) {
        if (container.flightType !== null) {
          newFlightType = container.flightType;
        }
      }
    }
  } else if (fare && fare.dataType === 'EMPTY_FROM_API') {
    if (container.flightType !== undefined) {
      if (container.flightType !== null) {
        newFlightType = container.flightType;
      }
    }
  }

  const data: $TSFixMe = {
    dataType: formatValue(fare.dataType),
    trackingParameters: getStaticParameters(v2DeepLinkApiSettings),
    passengers: formatValue(passengerCount),
    origin,
    destination,
    travelClass: formatValue(travelClass),
    currencyCode: formatValue(currencyCode),
    journeyType: formatValue(journeyType),
    flightType: newFlightType,
    airlineCode: formatValue(airlineIataCode),
    farenetTravelClass: formatValue(fare.farenetTravelClass || travelClass),
    useMiles: useMiles === undefined ? Boolean(fare.redemption) : useMiles,
    ...fare?.paramsTrackingExtra
  };

  if (state.originCity) {
    data.originCity = state.originCity;
  }
  if (state.destinationCity) {
    data.destinationCity = state.destinationCity;
  }

  if (fare.airlineCode) {
    data.airline = fare.airlineCode;
  }
  if (v2DeepLinkApiSettings?.includeHotelId === true && fare?.hotelId) {
    data.hotelId = fare.hotelId;
  }

  // Populate origin and destination from filters when component does not has data
  if (fare.dataType === 'EMPTY_FROM_API') {
    let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;
    const today = dayjs();
    if (!departureDate || departureDate.isBefore(today)) {
      departureDate = today;
    }
    let returnDate = fare.returnDate ? dayjs(fare.returnDate) : null;
    if (departureDate && !returnDate) {
      // If a return date is not defined an implicit return date of a week (7 days)
      // after the departure date is sent.
      returnDate = departureDate.add(7, 'day');
    }
    data.departureDate = formatDate(departureDate);
    data.returnDate = formatDate(returnDate);
    if (journeyType === 'ew' || journeyType === undefined) {
      data.journeyType = 'rt';
    }

    return data;
  }
  // Populate data when fare is not empty
  return {
    ...data,
    departureDate: formatValue(fare.departureDate),
    returnDate: formatValue(fare.returnDate),
    formattedDepartureDate: formatValue(fare.formattedDepartureDate),
    formattedReturnDate: formatValue(fare.formattedReturnDate),
    totalPrice: formatValue(fare.totalPrice),
    popularity: formatValue(fare.popularity),
    searchDate: formatValue(fare.searchDate),
    usdTotalPrice: formatValue(fare.usdTotalPrice),
    formattedTotalPrice: formatValue(fare.formattedTotalPrice),
    formattedUsdTotalPrice: formatValue(fare.formattedUsdTotalPrice),
    flightDeltaDays: formatValue(fare.flightDeltaDays)
  };
}

export function openPopup(fare: $TSFixMe) {
  let momentFormattedDeparture = null;
  let momentFormattedReturn = null;
  if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    // eslint-disable-next-line
    const fcRuntime = window.EM.___fcRuntime;
    if (fcRuntime && fcRuntime.vendors && fcRuntime.vendors.moment) {
      const infameMomentPleaseDontImportMeNever = fcRuntime.vendors.moment;
      momentFormattedDeparture =
        fare && fare.departureDate && infameMomentPleaseDontImportMeNever(fare.departureDate);
      momentFormattedReturn =
        fare && fare.returnDate && infameMomentPleaseDontImportMeNever(fare.returnDate);
    } else {
      logger.warn(
        `INTEGRATION_ACTIONS: **OpenPopupAction** window.EM.___fcRuntime.vendors.moment not 
            available as global variable and the **Booking Popup** needed it`
      );
    }
  }
  const data = { ...fare };
  if (momentFormattedDeparture && momentFormattedDeparture.isValid()) {
    data.departureDate = momentFormattedDeparture;
  }
  if (momentFormattedReturn && momentFormattedReturn.isValid()) {
    data.returnDate = momentFormattedReturn;
  }

  publish('em.booking.open', data);
}

export function getPublisherData({ baseline, fare, callToActionLabel }: $TSFixMe) {
  const journeyTypeMap = journeyTypeToLongValue() as $TSFixMe;
  let data: $TSFixMe = {
    currencyCode: fare.currencyCode ? formatValue(fare.currencyCode) : baseline.currencyCode,
    callToActionLabel: formatValue(callToActionLabel),
    departureDate: fare.departureDate ? formatValue(fare.departureDate) : baseline.departureDate,
    fareClass: fare.farenetTravelClass
      ? formatValue(fare.farenetTravelClass.toUpperCase())
      : baseline.fareClass,
    journeyType:
      fare.journeyType && journeyTypeMap[fare.journeyType]
        ? journeyTypeMap[fare.journeyType]
        : baseline.journeyType,
    flexibleDates: baseline.flexibleDates,
    promoCode: baseline.promoCode,
    returnDate: fare.returnDate ? fare.returnDate : baseline.returnDate,
    siteEdition: baseline.siteEdition,
    totalPrice: formatValue(fare.totalPrice),
    totalPriceUSD: formatValue(fare.usdTotalPrice),
    passengerCount: baseline.passengerCount,
    destinationAirportIataCode: baseline.destinationAirportIataCode,
    originAirportIataCode: baseline.originAirportIataCode,
    useMiles: baseline.useMiles !== undefined ? baseline.useMiles : Boolean(fare.redemption)
  };

  if (fare.journeyType && data.journeyType === 'n/a') {
    data.journeyType = fare.journeyType;
  }

  if (fare.redemption !== undefined) {
    if (fare.redemption.unit.toUpperCase() === 'MILES') {
      data.miles = true;
      data.taxAmount = fare.redemption.taxAmount;
    }
  }

  if (fare.airlineCode) {
    data.airlineCode = fare.airlineCode;
  }
  if (baseline.tagName) {
    data.tagName = baseline.tagName;
  }
  if (baseline.originCity) {
    data.originCity = baseline.originCity;
  }
  if (baseline.destinationCity) {
    data.destinationCity = baseline.destinationCity;
  }
  return data;
}

function evaluateParameter(fare: $TSFixMe, param: $TSFixMe) {
  if (
    param &&
    (typeof param === 'string' || param instanceof String) &&
    param.indexOf('@@') !== -1
  ) {
    const paramEvaluation = param.split('.');

    if (paramEvaluation && paramEvaluation.length) {
      const paramsEvalLength = paramEvaluation[1].length;
      // fieldFare contain the field from api
      const fieldFare = paramEvaluation[1].substr(0, paramsEvalLength - 2);

      if (fare) {
        return fare[fieldFare];
      }
    }
  }

  return param;
}

async function buildQuery(
  container: $TSFixMe,
  fare: $TSFixMe,
  origin: string,
  destination: string,
  airlines: boolean,
  state: $TSFixMe
) {
  const {
    deviceCategory,
    siteEdition,
    flightType,
    language,
    currencyCode,
    v2DeepLinkApiSettings: { params },
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    originDestinationLogic
  } = container;

  // Get external platfom parameters
  const {
    travelClass,
    flexibleDates,
    promoCode,
    extraInfo,
    customFlexibleDatesValue,
    customPassengers
  } = params;
  const { v2DeepLinkApiSettings } = container;

  if (!deviceCategory) {
    logger.warn('INTEGRATION__DEEPLINK: **device** is not defined.');
  }
  if (!siteEdition) {
    logger.warn('INTEGRATION__DEEPLINK: **siteEdition** is not defined.');
  }
  if (!travelClass) {
    logger.warn('INTEGRATION__DEEPLINK: **travelClass** is not defined.');
  }
  if (promoCode === null) {
    logger.warn('INTEGRATION__DEEPLINK: **promoCode** is not defined.');
  }
  if (!extraInfo) {
    logger.warn('INTEGRATION__DEEPLINK: **extraInfo** is not defined.');
  }

  if (!fare) {
    logger.warn('INTEGRATION__DEEPLINK: **fare** is empty.');
  }

  // https://everymundo.atlassian.net/browse/FC-2908
  let newFlightType = flightType;
  if (
    (fare && fare.origin !== undefined && fare.dataType === undefined) ||
    (fare && fare.origin !== undefined && fare.dataType === 'DATA_API')
  ) {
    //exist fare data
    if (fare.journeyType) {
      newFlightType =
        flightTypeMap[fare.journeyType] === undefined
          ? fare.journeyType
          : flightTypeMap[fare.journeyType];
    } else {
      if (container.flightType !== undefined) {
        if (container.flightType !== null) {
          newFlightType = container.flightType;
        }
      }
    }
  } else if (fare && fare.dataType === 'EMPTY_FROM_API') {
    if (container.flightType !== undefined) {
      if (container.flightType !== null) {
        newFlightType = container.flightType;
      } else {
        newFlightType = 'ROUND_TRIP';
      }
    }
  }

  let newExtraInfo = { ...extraInfo };
  if (
    v2DeepLinkApiSettings?.includeLocationType === true &&
    fare?.originLocationType !== undefined &&
    fare?.originLocationType !== null &&
    fare?.destinationLocationType !== undefined &&
    fare?.destinationLocationType !== null
  ) {
    newExtraInfo['originLocationType'] = fare?.originLocationType;
    newExtraInfo['destinationLocationType'] = fare?.destinationLocationType;
  }

  let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;

  if (!departureDate) {
    departureDate = dayjs().add(container.departureDateStart, 'day');
  }
  let returnDate = fare.returnDate ? dayjs(fare.returnDate) : null;
  if (departureDate && !returnDate) {
    returnDate = departureDate.add(7, 'day');
  }

  let query: QueryType = {
    origin: (fare && fare.origin) || origin || '',
    destination: (fare && fare.destination) || destination || '',
    departure: formatDate(departureDate),
    return: fare && newFlightType === 'ROUND_TRIP' ? formatDate(returnDate) : '',
    flightType: newFlightType,
    siteEdition,
    language,
    passengers: {
      passenger1: 1
    },
    currencyCode: (fare && fare.currencyCode) || currencyCode || '',
    travelClass: fare && evaluateParameter(fare, travelClass),
    promoCode: fare && evaluateParameter(fare, promoCode),
    device: (deviceCategory && deviceCategory.toUpperCase()) || 'DESKTOP',
    extraInfo: newExtraInfo,
    trackingParameters: getStaticParameters(v2DeepLinkApiSettings)
  };

  const flexibleDatesValue =
    fare &&
    (state.dateRange.checkedFlexibleDates ??
      container.flexibleDates ??
      fare.flexibleDates ??
      evaluateParameter(fare, flexibleDates));

  if (flexibleDatesValue !== 'n/a') {
    query = { ...query, flexibleDates: flexibleDatesValue };
  }

  if (state.passengers && Array.isArray(state.passengers)) {
    query = {
      ...query,
      passengers: {
        passenger1: state.passengers[0],
        passenger2: state.passengers[1],
        passenger3: state.passengers[2],
        passenger4: state.passengers[3],
        passenger5: state.passengers[4],
        passenger6: state.passengers[5],
        passenger7: state.passengers[6]
      }
    };
  }
  if (airlines !== null && airlines !== undefined && origin !== '' && destination !== '') {
    query = { ...query, airlines: fare.airlineCode };
  }

  //INFO: https://tinyurl.com/29czgxtu
  //INFO: Custom payloads parameters in order to simulate the booking popup deep link
  if (v2DeepLinkApiSettings?.includeOriginDestinationInfo === true) {
    let originInfo = null;
    let destinationInfo = null;

    const originComplementaryData = await getAirports(
      {
        ...prePopulation.airport,
        airlineIataCode: prePopulation.airlineIataCode,
        language: prePopulation.languageCode,
        routeOption: { airportType: 'ORIGIN' }
      },
      integrationSettings,
      meta,
      pageContext
    );

    for (let item of originComplementaryData) {
      if (item?.iataCode === origin) {
        originInfo = item;
      }
      if (item?.iataCode === destination) {
        destinationInfo = item;
      }
      if (originInfo !== null && destinationInfo !== null) {
        break;
      }
    }

    if (destinationInfo === null) {
      if (originDestinationLogic === OriginDestinationLogic.DIFFERENT) {
        const destinationComplementaryData = await getAirports(
          {
            ...prePopulation.airport,
            airlineIataCode: prePopulation.airlineIataCode,
            language: prePopulation.languageCode,
            routeOption: {
              airportType: 'DESTINATION',
              selectedOrigin: fare.origin
            }
          },
          integrationSettings,
          meta,
          pageContext
        );

        for (let item of destinationComplementaryData) {
          if (item?.iataCode === destination) {
            destinationInfo = item;
            break;
          }
        }
      }
    }
    if (originInfo !== null) {
      if (originInfo?.zoneId || originInfo?.locationId) {
        query['originInfo'] = {
          extraInfo: {
            zoneId: originInfo?.zoneId,
            locationId: originInfo?.locationId
          }
        };
      }
      if (originInfo?.city) {
        query['originInfo'] = {
          city: originInfo?.city,
          extraInfo: { type: originInfo?.extraInfo?.type }
        };
      }
    } else {
      console.warn('INTEGRATION__DEEPLINK: Missing originInfo');
      query['originInfo'] = {};
    }

    if (destinationInfo !== null) {
      if (destinationInfo?.zoneId || destinationInfo?.locationId) {
        query['destinationInfo'] = {
          extraInfo: {
            zoneId: destinationInfo?.zoneId,
            locationId: destinationInfo?.locationId
          }
        };
      }
      if (destinationInfo?.city) {
        query['destinationInfo'] = {
          city: destinationInfo?.city,
          extraInfo: { type: destinationInfo?.extraInfo?.type }
        };
      }
    } else {
      console.warn('INTEGRATION__DEEPLINK: Missing destinationInfo');
      query['destinationInfo'] = {};
    }
    query['origin'] = origin;
    query['destination'] = destination;
  }

  //INFO: https://tinyurl.com/29czgxtu
  //INFO: Custom payloads parameters in order to simulate the booking popup deep link
  if (v2DeepLinkApiSettings?.includeVacationInfo === true) {
    if (v2DeepLinkApiSettings?.params?.customVacationsInfo !== undefined) {
      query['vacations'] = {
        ...v2DeepLinkApiSettings?.params?.customVacationsInfo
      };
    }
    if (v2DeepLinkApiSettings?.params?.optionalsDeeplinkParameters !== undefined) {
      const optionalsDeeplinkParameters =
        v2DeepLinkApiSettings?.params?.optionalsDeeplinkParameters;
      for (let index = 0; index < optionalsDeeplinkParameters.length; index++) {
        const element = optionalsDeeplinkParameters[index];
        switch (element.type) {
          case 'area': {
            const data = element.data;
            for (let i = 0; i < data.length; i++) {
              const e = data[i];
              switch (e) {
                case 'area1': {
                  query['vacations'] = {
                    ...query['vacations'],
                    area1: `${element.prefix}:${query['origin']}`
                  };
                  break;
                }
                case 'area2': {
                  query['vacations'] = {
                    ...query['vacations'],
                    area2: `${element.prefix}:${query['destination']}`
                  };
                  break;
                }
              }
            }
            break;
          }
          case 'date': {
            for (let i = 0; i < element.data.length; i++) {
              const e = element.data[i];
              switch (e) {
                case 'date1': {
                  const departureDate = dayjs(fare.departureDate, 'YYYY-MM-DD').toDate();
                  query['vacations'] = {
                    ...query['vacations'],
                    date1: formatDate(departureDate, element.dateFormat)
                  };
                  break;
                }
                case 'date2': {
                  const returnDate = dayjs(fare.returnDate, 'YYYY-MM-DD').toDate();
                  query['vacations'] = {
                    ...query['vacations'],
                    date2: formatDate(returnDate, element.dateFormat)
                  };
                  break;
                }
              }
            }
            break;
          }
          case 'air_room_car_radio': {
            query['vacations'] = {
              ...query['vacations'],
              air_room_car_radio: 'air_room'
            };
            break;
          }
        }
      }
    }
  }

  //INFO: https://tinyurl.com/29czgxtu
  //INFO: Custom payloads parameters in order to simulate the booking popup deep link
  if (customPassengers !== undefined) {
    query['passengers'] = {
      ...customPassengers
    };
  }
  if (customFlexibleDatesValue !== undefined && typeof customFlexibleDatesValue === 'boolean') {
    query['flexibleDates'] = customFlexibleDatesValue;
  }
  return query;
}

export async function redirect({ container, fare, state }: $TSFixMe) {
  const { origin, destination, airlines } = state;
  const query = await buildQuery(container, fare, origin, destination, airlines, state);
  const { v2DeepLinkApiSettings } = container;
  const newV2DeepLinkApiSettings = { ...v2DeepLinkApiSettings };
  let error = '';
  if (!newV2DeepLinkApiSettings.method) {
    error += 'INTEGRATION__DEEPLINK: **method** is not defined. ';
  }
  if (!newV2DeepLinkApiSettings.endPoint) {
    error += 'INTEGRATION__DEEPLINK: **endPoint** is not defined.';
  }
  if (
    newV2DeepLinkApiSettings.endPoint.search('%%airlineCode%%') !== -1 ||
    newV2DeepLinkApiSettings.endPoint.search('@@airlineIataCode@@') !== -1
  ) {
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '%%airlineCode%%',
      fare.airlineCode
    );
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '@@airlineIataCode@@',
      fare.airlineCode
    );
  }
  if (!newV2DeepLinkApiSettings.headers) {
    newV2DeepLinkApiSettings.headers = {};
  }
  if (error) {
    logger.error(error);
  } else {
    axios({
      method: newV2DeepLinkApiSettings.method,
      url: `${newV2DeepLinkApiSettings.endPoint}`,
      headers: { ...newV2DeepLinkApiSettings.headers },
      data: query
    })
      .then((response) => doRedirect(response.data))
      .catch((err) => {
        logger.error(
          annotateAndLogError(
            err,
            'INTEGRATION__DEEPLINK',
            false,
            false,
            container.integrationSettings,
            container.meta
          )
        );
      });
  }
}

function isEnableTrackingEvent(nameEvent: string, enableEventsName: Array<string>) {
  for (let index = 0; index < enableEventsName.length; index++) {
    const element = enableEventsName[index];
    if (element.toUpperCase() === nameEvent) {
      return true;
    }
  }
  return false;
}

function sendTrackingEvent(
  nameEvent: string,
  actionType: string,
  payload: any,
  enableEventsName: Array<string>,
  send: any
) {
  if (enableEventsName && enableEventsName.length !== 0) {
    if (isEnableTrackingEvent(actionType, enableEventsName)) {
      return send(nameEvent, payload);
    }
  } else {
    return send(nameEvent, payload);
  }
}

export function useTrackingChange() {
  const newContainer: $TSFixMe = useTracking();
  const { useMiles } = newContainer;
  const container = { ...newContainer };
  delete container.useMiles;
  const { eventType, fareItemInteractionType } = container;

  const {
    v2DeepLinkApiSettings: { params = {} }
  } = container;

  // Get external platfom parameters
  const { extraInfo } = params;

  const track = useCallback(
    (state: $TSFixMe, action: $TSFixMe, actualState: $TSFixMe) => {
      const getFlexibleDatesValue = () => {
        if (container.flexibleDates !== NOT_AVAILABLE) {
          if (actualState.dateRange.checkedFlexibleDates !== undefined) {
            return actualState.dateRange.checkedFlexibleDates;
          } else {
            return container.flexibleDates;
          }
        } else {
          return NOT_AVAILABLE;
        }
      };
      const baseline: $TSFixMe = {
        currencyCode: container.currencyCode,
        departureDate: state.dateRange.start
          ? formatDate(state.dateRange.start)
          : formatDate(container.departureDateStart),
        destinationAirportIataCode: formatValue(state.destination.value),
        fareClass: formatValue(actualState.travelClass),
        flexibleDates: formatValue(getFlexibleDatesValue()),
        journeyType: formatValue(state.flightType),
        originAirportIataCode: formatValue(state.origin.value),
        promoCode: formatValue(container.promoCode),
        returnDate: formatDate(state.dateRange.end),
        siteEdition: container.siteEdition,
        flightStopCount: formatValue(actualState.flightStopCount),
        useMiles: useMiles
      };

      if (extraInfo && extraInfo.useMiles) {
        baseline.miles = true;
      }

      // Tag name is optional
      if (container.tagName) {
        baseline.tagName = container.tagName;
      }

      const { enableCityCodes } = container.trackingConfig ? container.trackingConfig : false;

      let journeyTypeMap;
      let defaultTracking;
      let fareState;
      if (action.payload && action.payload.fare) {
        const { fare } = action.payload;
        journeyTypeMap = journeyTypeToShortValue() as $TSFixMe;

        let origin,
          destination = '';
        if (enableCityCodes && fare.aditionalOrigin !== '' && fare.aditionalDestination !== '') {
          origin = fare.aditionalOrigin;
          destination = fare.aditionalDestination;
        } else {
          origin = fare.origin ? fare.origin : state.origin.value;
          destination = fare.destination ? fare.destination : state.destination.value;
        }
        fareState = {
          ...state,
          destination: destination,
          origin: origin,
          journeyType: fare.journeyType ? fare.journeyType : journeyTypeMap[state.flightType],
          flightType: fare.flightType ? fare.flightType : state.flightType,
          currencyCode: fare.currencyCode ? fare.currencyCode : container.currencyCode,
          travelClass: fare.travelClass ? fare.travelClass : state.travelClass
        };

        let passengerCount = formatValue(container.passengerCount);
        if (fareState.passengers && Array.isArray(fareState.passengers)) {
          passengerCount = 0;
          fareState.passengers.forEach((element: any) => {
            passengerCount += element;
          });
        }
        defaultTracking = {
          ...baseline,
          destinationAirportIataCode: formatValue(fareState.destination),
          originAirportIataCode: formatValue(fareState.origin),
          passengerCount: passengerCount,
          returnDate: NOT_AVAILABLE
        };
      }

      const send = (name: $TSFixMe, changes: $TSFixMe) => {
        publish(`em.fare.${eventType}.${name}`, {
          ...baseline,
          ...changes
        });
      };

      const enableEventsName =
        container.trackingConfig !== undefined ? container.trackingConfig.enable : [];

      switch (action.type) {
        case 'ORIGIN_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'origin',
              action.type,
              { originAirportIataCode: action.payload.value, ...date },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'origin',
            action.type,
            { originAirportIataCode: formatValue(action.payload.value) },
            enableEventsName,
            send
          );
          break;
        }
        case 'ORIGIN_CLEAR_SECONDARY': {
          sendTrackingEvent(
            'origin',
            action.type,
            { originAirportIataCode: formatValue() },
            enableEventsName,
            send
          );
          break;
        }
        case 'DESTINATION_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'destination',
              action.type,
              { destinationAirportIataCode: action.payload.value, ...date },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'destination',
            action.type,
            { destinationAirportIataCode: formatValue(action.payload.value) },
            enableEventsName,
            send
          );
          break;
        }
        case 'DESTINATION_CLEAR_SECONDARY': {
          sendTrackingEvent(
            'destination',
            action.type,
            { destinationAirportIataCode: formatValue() },
            enableEventsName,
            send
          );
          break;
        }
        case 'SWAP_LOCATIONS': {
          sendTrackingEvent(
            'origin',
            action.type,
            {
              originAirportIataCode: state.destination.value,
              destinationAirportIataCode: state.origin.value
            },
            enableEventsName,
            send
          );
          sendTrackingEvent(
            'destination',
            action.type,
            {
              originAirportIataCode: state.destination.value,
              destinationAirportIataCode: state.origin.value
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'SORTING_CHANGE': {
          sendTrackingEvent(
            'sort',
            action.type,
            { sortBy: action.payload.label },
            enableEventsName,
            send
          );
          break;
        }
        case 'BUDGET_CHANGE': {
          sendTrackingEvent(
            'budget',
            action.type,
            { budgetMaximumAmount: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRIP_DURATION_CHANGE': {
          sendTrackingEvent(
            'tripDuration',
            action.type,
            { tripDuration: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRAVELER_CHANGE': {
          let selectedItems: $TSFixMe[] = [];

          Object.keys(action.payload).forEach((key) => {
            selectedItems = action.payload[key].selectedItems
              .filter((i: { value: string }) => i.value !== 'any')
              .map((i: any) => i);
          });
          sendTrackingEvent(
            'select-travel-interest',
            action.type,
            { interest: selectedItems },
            enableEventsName,
            send
          );
          break;
        }
        case 'NUMBER_STOPS_CHANGE': {
          sendTrackingEvent(
            'select-stop',
            action.type,
            { flightStopCount: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'RATING_CHANGE': {
          sendTrackingEvent(
            'rating',
            action.type,
            { rating: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'FLIGHT_TYPE_CHANGE': {
          if (state.histogramType !== null && state.histogramType !== undefined) {
            let date = {
              departureDate: NOT_AVAILABLE,
              returnDate: NOT_AVAILABLE
            };
            sendTrackingEvent(
              'journeyType',
              action.type,
              { journeyType: action.payload, ...date },
              enableEventsName,
              send
            );
            return;
          }
          sendTrackingEvent(
            'journeyType',
            action.type,
            { journeyType: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'TRAVEL_CLASS_CHANGE': {
          sendTrackingEvent(
            'travelClass',
            action.type,
            { travelClass: action.payload },
            enableEventsName,
            send
          );
          break;
        }
        case 'MONTH_CHANGE_HISTOGRAM': {
          let date = {
            departureDate: action.payload.dateRange.start
              ? formatDate(action.payload.dateRange.start)
              : NOT_AVAILABLE,
            returnDate: action.payload.dateRange.end
              ? formatDate(action.payload.dateRange.end)
              : NOT_AVAILABLE
          };
          sendTrackingEvent(
            'month',
            action.type,
            { month: dayjs(action.payload.start).month(), ...date },
            enableEventsName,
            send
          );
          break;
        }
        case 'MONTH_CHANGE': {
          sendTrackingEvent(
            'month',
            action.type,
            { month: dayjs(action.payload.start).month() },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_CHANGE_DEPARTURE': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          const noChange = departureDate === returnDate;
          sendTrackingEvent(
            'departure',
            action.type,
            {
              departureDate,
              returnDate: noChange ? NOT_AVAILABLE : returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_CHANGE_RETURN': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          sendTrackingEvent(
            'return',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_SET_START_DATE': {
          const departureDate = formatDate(action.payload.dateRange.selected);
          const returnDate = NOT_AVAILABLE;
          sendTrackingEvent(
            'departure',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_HISTOGRAM_SET_SUGGESTED_DATE': {
          const departureDate = formatDate(action.payload.dateRange.start);
          const returnDate = formatDate(action.payload.dateRange.end);
          sendTrackingEvent(
            'return',
            action.type,
            {
              departureDate,
              returnDate,
              flexibleDates: baseline.flexibleDates
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'DATE_RANGE_CHANGE': {
          const departureDate = formatDate(action.payload.start);
          const returnDate = formatDate(action.payload.end);

          const noChange = departureDate === returnDate;

          const type = action.payload.type ?? 'all';
          const isDeparture = type === 'all' || type === 'departure';
          const isReturn = type === 'all' || type === 'return';
          if (
            (isDeparture && departureDate !== baseline.departureDate) ||
            (baseline.flexibleDates !== 'n/a' &&
              state.dateRange.checkedFlexibleDates !== baseline.flexibleDates)
          ) {
            sendTrackingEvent(
              'departure',
              action.type,
              {
                departureDate,
                returnDate: noChange ? NOT_AVAILABLE : returnDate,
                flexibleDates: baseline.flexibleDates
              },
              enableEventsName,
              send
            );
          }
          if (
            (isReturn && returnDate !== baseline.returnDate) ||
            (isReturn &&
              returnDate !== 'n/a' &&
              baseline.flexibleDates !== 'n/a' &&
              state.dateRange.checkedFlexibleDates !== baseline.flexibleDates)
          ) {
            sendTrackingEvent(
              'return',
              action.type,
              {
                departureDate,
                returnDate,
                flexibleDates: baseline.flexibleDates
              },
              enableEventsName,
              send
            );
          }
          return undefined;
        }
        case 'LOAD_MORE': {
          sendTrackingEvent('load-more', action.type, {}, enableEventsName, send);
          break;
        }
        case 'CLEAR_ALL':
        case 'CLEAR_PARTIAL': {
          sendTrackingEvent(
            'clear-all',
            action.type,
            {
              filters: 'cleared'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'MORE_DEALS': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            { actionLabel: 'more-deals' },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_ARROW_LEFT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'left-arrow'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_ARROW_RIGHT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'right-arrow'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_SWIPE_LEFT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'swipe-left'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'CAROUSEL_SWIPE_RIGHT': {
          sendTrackingEvent(
            'more-deals',
            action.type,
            {
              actionLabel: 'swipe-right'
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'SEE_MORE': {
          const { fare } = action.payload;
          if (enableEventsName.length !== 0) {
            if (isEnableTrackingEvent(action.Type, enableEventsName)) {
              publish(
                `em.fare.book.${container.eventType}`,
                getPublisherData({
                  baseline: { ...defaultTracking, useMiles: useMiles },
                  fare,
                  callToActionLabel: 'See-more'
                })
              );
            }
          } else {
            publish(
              `em.fare.book.${container.eventType}`,
              getPublisherData({
                baseline: { ...defaultTracking, useMiles: useMiles },
                fare,
                callToActionLabel: 'See-more'
              })
            );
          }
          return undefined;
        }
        case 'FARE_CLICK': {
          const { fare, callToActionLabel } = action.payload;
          if (!fare) {
            return undefined;
          }
          if (typeof fare === 'object' && fare.constructor === Object) {
            if (fareItemInteractionType === 'deeplink') {
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  publish(
                    `em.fare.book.${eventType}`,
                    getPublisherData({
                      baseline: { ...defaultTracking, useMiles: useMiles },
                      fare,
                      callToActionLabel
                    })
                  );
                }
              } else {
                publish(
                  `em.fare.book.${eventType}`,
                  getPublisherData({
                    baseline: { ...defaultTracking, useMiles: useMiles },
                    fare,
                    callToActionLabel
                  })
                );
              }
              redirect({ container, state: fareState, fare });
            }
            if (fareItemInteractionType === 'popup') {
              openPopup(
                getPopupData({ container: { ...container, useMiles }, state: fareState, fare })
              );
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  publish(
                    `em.fare.popup.${eventType}`,
                    getPublisherData({
                      baseline: { ...defaultTracking, useMiles: useMiles },
                      fare,
                      callToActionLabel
                    })
                  );
                }
              } else {
                publish(
                  `em.fare.popup.${eventType}`,
                  getPublisherData({
                    baseline: { ...defaultTracking, useMiles: useMiles },
                    fare,
                    callToActionLabel
                  })
                );
              }
            }
          }
          return undefined;
        }
        case 'SELECT_MAP_DESTINATION': {
          sendTrackingEvent(
            'select-map-destination',
            action.type,
            {
              destinationAirportIataCode: action.payload.value,
              departureDate:
                action.payload.start !== undefined ? action.payload.start : NOT_AVAILABLE,
              returnDate: action.payload.end !== undefined ? action.payload.end : NOT_AVAILABLE
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'EXPAND_MAP': {
          sendTrackingEvent(
            'expand-map',
            action.type,
            { expanded: action.payload.value },
            enableEventsName,
            send
          );
          break;
        }
        case 'ZOOM': {
          const { oldZoom, newZoom, expanded } = action.payload;
          sendTrackingEvent(
            'zoom',
            action.type,
            { expanded, zoom: oldZoom > newZoom ? 'out' : 'in' },
            enableEventsName,
            send
          );
          break;
        }
        case 'TOGGLE_FARE_LIST': {
          sendTrackingEvent(
            'toggle-farelist',
            action.type,
            { action: action.payload.event },
            enableEventsName,
            send
          );
          break;
        }
        case 'AIRLINES_CHANGE': {
          sendTrackingEvent(
            'airlineCodes',
            action.type,
            { airlineCodes: action.payload.value },
            enableEventsName,
            send
          );
          break;
        }
        default: {
          return undefined;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newContainer, extraInfo, eventType, fareItemInteractionType]
  );

  return track;
}
